import React, { useEffect, useMemo, useRef, useState } from "react";
import { Filter, Heart, Search } from "lucide-react";
import "./calendar.css";
import BusinessProfile from "../BusinessProfile/BusinessProfile";
import { supabase } from "../../supabase_client.jsx";

// ===== Constants (kept outside component to avoid re-creating on every render) =====
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FILTER_OPTIONS = [
  { label: "Food", value: "Food" },
  { label: "Sports", value: "Sports" },
  { label: "Music", value: "Music" },
  { label: "Movies", value: "Movies" },
  { label: "Gaming", value: "Gaming" },
  { label: "Travel", value: "Travel" },
  { label: "Fitness", value: "Fitness" },
  { label: "Art", value: "Art" },
  { label: "Tech", value: "Tech" },
  { label: "Outdoors", value: "Outdoors" },
  { label: "Other", value: "Other" },
];

//convert date into YYYY-MM-DD
export function formatDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

export function formatDisplayDate(isoDate) {
  // Split manually to avoid UTC->local timezone shift
  const [year, month, day] = String(isoDate).split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function groupEventsByDateKey(events) {
  const grouped = {};
  (events || []).forEach((evt) => {
    const key = evt.date;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(evt);
  });
  return grouped;
}

export function buildSuggestions(events, searchQuery, limit = 8) {
  const q = (searchQuery || "").trim().toLowerCase();
  if (!q || !Array.isArray(events)) return [];

  return events
    .filter((evt) => (evt?.name || "").toLowerCase().includes(q))
    .slice(0, limit);
}

export default function Calendar({ currentUser, onViewSelect }) {
  const [events, setEvents] = useState(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [view, setView] = useState("month"); //default view
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [eventsByDateKey, setEventsByDateKey] = useState({});
  const [viewingBusinessId, setViewingBusinessId] = useState(null);
  
  // dropdown search state feature
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const searchBoxRef = useRef(null);

  // ===== Filters dropdown =====
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]); // ["trivia","sports","music","food&beverage"]
  const filterRef = useRef(null);

  //month logic
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // ===== Right side popup + hover refs =====
  const containerRef = useRef(null);

  // Hover logic
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);

  const handleEventMouseEnter = (eventData, e) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    const targetRect = e.currentTarget.getBoundingClientRect();
    if (!containerRect) return;

    const top = targetRect.bottom - containerRect.top + 4;
    const left = targetRect.left - containerRect.left;

    setHoveredEvent(eventData);
    setHoverPosition({ top, left });
  };

  const handleEventMouseLeave = () => {
    setHoveredEvent(null);
    setHoverPosition(null);
  };

  // Side panel logic
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Bookmark/Yes data
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [yesIds, setYesIds] = useState([]);

  // ===== Event social card state (connections going + other attendees) =====
  const [connectionsGoing, setConnectionsGoing] = useState([]);
  const [suggestedGoing, setSuggestedGoing] = useState([]);
  const [socialLoading, setSocialLoading] = useState(false);

  // ===== Safety confirmation modal (reused pattern from Connections page) =====
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  // pendingAction: { type: "ADD", receiverId: string }

  // Tracks which "Add" buttons have been confirmed in this popup (so they flip to "Added")
  const [addedConnectionIds, setAddedConnectionIds] = useState([]);

  useEffect(() => {
    // When switching events (or closing/reopening), reset local "Added" button states
    setAddedConnectionIds([]);
  }, [selectedEvent?.id]);

  const userId = currentUser?.user?.id || null;

  // ===== Notifications (unread dot) =====
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

  useEffect(() => {
    if (!userId) {
      setHasUnreadNotifications(false);
      return;
    }

    const lastSeenKey = `dwyg_notifications_last_seen_${userId}`;
    const lastSeenIso = localStorage.getItem(lastSeenKey);
    const lastSeenMs = lastSeenIso ? Date.parse(lastSeenIso) : 0;
    const nowMs = Date.now();

    let cancelled = false;

    const compute = async () => {
      try {
        // 1) Pending connection requests (unread if created after last seen)
        const { data: reqRows, error: reqErr } = await supabase
          .from("connection_requests")
          .select("id,created_at")
          .eq("receiver_id", userId)
          .eq("status", "pending")
          .order("created_at", { ascending: false });

        if (reqErr) {
          console.warn("Unread notifications: connection_requests query failed:", reqErr.message);
        }

        const hasUnreadRequests = (reqRows || []).some((r) => {
          const t = r?.created_at ? Date.parse(r.created_at) : 0;
          return t && t > lastSeenMs;
        });

        // 2) Upcoming bookmarked/going events (unread when they enter the 7-day window)
        const notifyWindowMs = 7 * 24 * 60 * 60 * 1000;
        const watchIds = new Set([...(bookmarkedIds || []), ...(yesIds || [])]);

        const hasUnreadUpcoming = (events || []).some((e) => {
          const idNum = Number(e?.id);
          if (!Number.isFinite(idNum) || !watchIds.has(idNum)) return false;
          const [ey, em, ed] = (e?.date || "").split("-").map(Number); const d = ey ? new Date(ey, em - 1, ed) : null;
          if (!d || Number.isNaN(d.getTime())) return false;
          const eventMs = d.getTime();
          if (eventMs <= nowMs) return false;
          const notifyAt = eventMs - notifyWindowMs;
          return notifyAt <= nowMs && notifyAt > lastSeenMs;
        });

        if (!cancelled) setHasUnreadNotifications(Boolean(hasUnreadRequests || hasUnreadUpcoming));
      } catch (err) {
        console.warn("Unread notifications compute failed:", err);
        if (!cancelled) setHasUnreadNotifications(false);
      }
    };

    compute();
    return () => {
      cancelled = true;
    };
  }, [userId, events, bookmarkedIds, yesIds]);


  // Profile setup status (kept)
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [dismissedSetupToast, setDismissedSetupToast] = useState(false);

  // NEW: centered setup modal + YES-block modal
  const [showSetupGateModal, setShowSetupGateModal] = useState(false);
  const [showYesBlockedModal, setShowYesBlockedModal] = useState(false);

  // check/create profile meta row + realtime subscription so the modal
  // dismisses immediately when the user completes their profile (no logout needed)
  useEffect(() => {
    if (!userId) {
      setNeedsProfileSetup(false);
      return;
    }

    const applyProfileStatus = (hasSetup) => {
      const notSetup = !hasSetup;
      setNeedsProfileSetup(notSetup);
      if (notSetup && !dismissedSetupToast) {
        setShowSetupGateModal(true);
      } else {
        // Profile is complete — always hide both modals
        setShowSetupGateModal(false);
        setShowYesBlockedModal(false);
      }
    };

    const checkProfileSetup = async () => {
      const { data, error } = await supabase
        .from("user_profile_meta")
        .select("has_setup_profile")
        .eq("user_id", userId)
        .single();

      // If row missing (or select fails), create a default row
      if (error || !data) {
        const { error: upErr } = await supabase.from("user_profile_meta").upsert({
          user_id: userId,
          has_setup_profile: false,
          preferences: [],
        });

        if (upErr) {
          console.warn("Meta upsert failed:", upErr.message);
        }

        applyProfileStatus(false);
        return;
      }

      applyProfileStatus(data.has_setup_profile);
    };

    checkProfileSetup();

    // Realtime: dismiss the popup the moment has_setup_profile becomes true
    // (e.g. user completes their profile while still signed in)
    const channel = supabase
      .channel(`profile-meta-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_profile_meta",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new;
          if (updated && typeof updated.has_setup_profile !== "undefined") {
            applyProfileStatus(updated.has_setup_profile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, dismissedSetupToast]);

  // ===== Get Events from Supabase =====
  async function GetEvents() {
    // Always fetch all approved events + their categories together
    const { data: eventsData, error: eventsError } = await supabase
      .from('events')
      .select(`
        *,
        event_category (
          category
        )
      `)
      .eq('is_approved', true);

    if (eventsError) { console.error('GetEvents error:', eventsError); return; }

    let approved = eventsData || [];

    // Normalize any stored category value to match our filter option values
    function normalizeCategory(cat) {
      if (!cat) return '';
      const c = cat.toLowerCase().trim();
      if (c === 'food&beverage' || c === 'food & beverage' || c === 'food') return 'Food';
      if (c === 'sports') return 'Sports';
      if (c === 'music') return 'Music';
      if (c === 'movies') return 'Movies';
      if (c === 'gaming') return 'Gaming';
      if (c === 'travel') return 'Travel';
      if (c === 'fitness') return 'Fitness';
      if (c === 'art') return 'Art';
      if (c === 'tech') return 'Tech';
      if (c === 'outdoors') return 'Outdoors';
      if (c === 'other') return 'Other';
      return cat.charAt(0).toUpperCase() + cat.slice(1);
    }

    // If filters are active, keep only events whose categories overlap with selectedFilters
    if (selectedFilters.length > 0) {
      approved = approved.filter((evt) => {
        const cats = (evt.event_category || []).map((c) => normalizeCategory(c.category));
        return cats.some((c) => selectedFilters.includes(c));
      });
    }

    // Strip the nested event_category array before storing (keeps shape consistent)
    const cleaned = approved.map(({ event_category: _ec, ...rest }) => rest);
    setEvents(cleaned);
    setEventsByDateKey(groupEventsByDateKey(cleaned));
  }

  // Re-fetch when filters change
  useEffect(() => {
    GetEvents();
  }, [selectedFilters]);

  // Search Bar suggested dropdown
  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !Array.isArray(events)) return [];

    return events
      .filter((evt) => (evt?.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [searchQuery, events]);

  const selectEventFromSearch = (evt) => {
    if (!evt) return;

    setSelectedEvent(evt);

    const [sy, sm, sd] = (evt.date || "").split("-").map(Number); const d = new Date(sy, sm - 1, sd);
    if (!isNaN(d)) setCurrentDate(d);

    setSearchQuery(evt.name || "");
    setIsSearchOpen(false);
    setHighlightIndex(-1);
  };

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(e.target)) {
        setIsSearchOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // Close filter dropdown on outside click
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!filterRef.current) return;
      if (!filterRef.current.contains(e.target)) {
        setIsFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const handleEventClick = (eventData) => {
    setSelectedEvent(eventData);
  };

  const closeDetailPanel = () => {
    setSelectedEvent(null);
  };

  // Load bookmarks
  useEffect(() => {
    if (!userId) {
      setBookmarkedIds([]);
      return;
    }

    const loadBookmarks = async () => {
      const { data, error } = await supabase
        .from("bookmarked_events")
        .select("event_id")
        .eq("user_id", userId);

      if (error) {
        console.error("Failed to load bookmarks:", error.message);
        setBookmarkedIds([]);
        return;
      }

      if (data) {
        setBookmarkedIds(data.map((row) => Number(row.event_id)));
      }
    };

    loadBookmarks();
  }, [userId]);

  // Load yes events
  useEffect(() => {
    if (!userId) {
      setYesIds([]);
      return;
    }

    const loadYes = async () => {
      const { data, error } = await supabase
        .from("marked_yes_events")
        .select("event_id")
        .eq("user_id", userId);

      if (error) {
        console.error("Failed to load yes events:", error.message);
        setYesIds([]);
        return;
      }

      if (data) {
        setYesIds(data.map((row) => Number(row.event_id)));
      }
    };

    loadYes();
  }, [userId]);

  // Bookmark toggle (Save for Later)
  const toggleBookmark = async (eventIdRaw) => {
    if (!userId) return;

    const eventId = Number(eventIdRaw);
    const isBookmarked = bookmarkedIds.includes(eventId);

    if (isBookmarked) {
      const { error } = await supabase
        .from("bookmarked_events")
        .delete()
        .eq("user_id", userId)
        .eq("event_id", eventId);

      if (error) {
        console.error("Failed to remove bookmark:", error.message);
        return;
      }

      setBookmarkedIds((prev) => prev.filter((id) => id !== eventId));
    } else {
      const { error } = await supabase.from("bookmarked_events").insert({
        user_id: userId,
        event_id: eventId,
      });

      if (error) {
        console.error("Failed to add bookmark:", error.message);
        return;
      }

      setBookmarkedIds((prev) => [...prev, eventId]);
    }
  };

  // ===== Helpers for event social card =====
  const userLabel = (u) => {
    if (!u) return "Unknown";
    return (
      u.display_name ||
      u.full_name ||
      u.username ||
      u.name ||
      u.email ||
      (u.user_id ? String(u.user_id).slice(0, 8) : "Unknown")
    );
  };

  // ========= Safety modal (same wording as Connections page) =========
  function requestAddConnection(receiverId) {
    if (!receiverId) return;
    setPendingAction({ type: "ADD", receiverId });
    setShowSafetyModal(true);
  }

  function cancelSafety() {
    setShowSafetyModal(false);
    setPendingAction(null);
  }

  async function doSendRequest(receiverId) {
    if (!userId || !receiverId) return;

    // If they already requested you, accept instead
    const { data: reverseRows } = await supabase
      .from("connection_requests")
      .select("id,status,requester_id,receiver_id")
      .eq("requester_id", receiverId)
      .eq("receiver_id", userId)
      .limit(1);

    const reverse = (reverseRows || [])[0];
    if (reverse && (reverse.status || "").toLowerCase() === "pending") {
      await supabase
        .from("connection_requests")
        .update({ status: "accepted", updated_at: new Date().toISOString() })
        .eq("id", reverse.id);
      return;
    }

    // Otherwise, create a pending request (avoid duplicates)
    const { data: existingRows } = await supabase
      .from("connection_requests")
      .select("id,status")
      .or(`and(requester_id.eq.${userId},receiver_id.eq.${receiverId}),and(requester_id.eq.${receiverId},receiver_id.eq.${userId})`)
      .limit(1);

    const existing = (existingRows || [])[0];
    if (existing) return;

    await supabase.from("connection_requests").insert({
      requester_id: userId,
      receiver_id: receiverId,
      status: "pending",
    });
  }

  async function confirmSafetyContinue() {
    if (!pendingAction) return;
    const action = pendingAction;
    setShowSafetyModal(false);
    setPendingAction(null);

    if (action.type === "ADD") {
      await doSendRequest(action.receiverId);

      // Flip the button to "Added" immediately after the user confirms
      setAddedConnectionIds((prev) => {
        const id = String(action.receiverId);
        return prev.includes(id) ? prev : [...prev, id];
      });

      // Refresh social lists so UI updates (pending/accepted might change who shows where)
      // Trigger by re-setting selectedEvent (no-op) to rerun effect below.
      setSelectedEvent((prev) => (prev ? { ...prev } : prev));
    }
  }

  const fetchUsersByIds = async (ids) => {
    if (!ids?.length) return new Map();

    // Try common PK column names without crashing if schema differs
    const tryCols = ["user_id", "id"];
    for (const col of tryCols) {
      const { data, error } = await supabase.from("users").select("*").in(col, ids);
      if (!error && Array.isArray(data)) {
        const map = new Map();
        data.forEach((u) => {
          const key = u.user_id ?? u.id;
          if (key) map.set(String(key), u);
        });
        return map;
      }
    }
    return new Map();
  };


  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!userId || !selectedEvent?.id) {
        setConnectionsGoing([]);
        setSuggestedGoing([]);
        return;
      }

      setSocialLoading(true);

      try {
        // 1) accepted connections for current user
        const { data: connRows, error: connErr } = await supabase
          .from("connection_requests")
          .select("requester_id, receiver_id, status")
          .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`)
          .eq("status", "accepted");

        if (connErr) throw connErr;

        const connectionIds = (connRows ?? [])
          .map((r) => (r.requester_id === userId ? r.receiver_id : r.requester_id))
          .filter(Boolean)
          .map(String);

        // 2) everyone marked YES for this event
        const { data: yesRows, error: yesErr } = await supabase
          .from("marked_yes_events")
          .select("user_id")
          .eq("event_id", Number(selectedEvent.id));

        if (yesErr) throw yesErr;

        const goingIds = (yesRows ?? []).map((r) => String(r.user_id)).filter(Boolean);

        const connectionSet = new Set(connectionIds);
        const goingSet = new Set(goingIds);

        const connectionsGoingIds = connectionIds.filter((id) => goingSet.has(id));
        const otherGoingIds = goingIds.filter((id) => id !== String(userId) && !connectionSet.has(id));

        // 3) fetch user rows for display
        const allIds = Array.from(
          new Set([...connectionsGoingIds, ...otherGoingIds])
        );

        const usersMap = await fetchUsersByIds(allIds);

        const toUser = (id) => usersMap.get(String(id)) || { user_id: id };

        const connGoingUsers = connectionsGoingIds.map(toUser);
        const otherGoingUsers = otherGoingIds.map(toUser);

        if (!cancelled) {
          setConnectionsGoing(connGoingUsers);
          setSuggestedGoing(otherGoingUsers);
        }
      } catch (e) {
        console.error("Failed to load event social sidebar:", e?.message || e);
        if (!cancelled) {
          setConnectionsGoing([]);
          setSuggestedGoing([]);
        }
      } finally {
        if (!cancelled) setSocialLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [userId, selectedEvent?.id]);


  const toggleYes = async (eventIdRaw) => {
    if (!userId) return;

    // Block YES until profile is set up
    if (needsProfileSetup) {
      setShowYesBlockedModal(true);
      return;
    }

    const eventId = Number(eventIdRaw);
    const isYes = yesIds.includes(eventId);

    if (isYes) {
      const { error } = await supabase
        .from("marked_yes_events")
        .delete()
        .eq("user_id", userId)
        .eq("event_id", eventId);

      if (error) {
        console.error("Failed to remove yes:", error.message);
        return;
      }

      setYesIds((prev) => prev.filter((id) => id !== eventId));
    } else {
      const { error } = await supabase.from("marked_yes_events").insert({
        user_id: userId,
        event_id: eventId,
      });

      if (error) {
        console.error("Failed to add yes:", error.message);
        return;
      }

      setYesIds((prev) => [...prev, eventId]);
    }
  };

  const isSelectedBookmarked =
    selectedEvent && bookmarkedIds.includes(Number(selectedEvent.id));

  const isSelectedYes =
    selectedEvent && yesIds.includes(Number(selectedEvent.id));

  // search handler
  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    if (!trimmed || !Array.isArray(events)) return;

    if (isSearchOpen && highlightIndex >= 0 && suggestions[highlightIndex]) {
      selectEventFromSearch(suggestions[highlightIndex]);
      return;
    }

    const lower = trimmed.toLowerCase();
    const match = events.find((evt) =>
      (evt?.name || "").toLowerCase().includes(lower)
    );

    if (match) selectEventFromSearch(match);
  };

  //switch between months/weeks/days
  const goToPrev = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() - 1);
      else if (view === "week") d.setDate(d.getDate() - 7);
      else d.setDate(d.getDate() - 1);
      return d;
    });
  };

  const goToNext = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() + 1);
      else if (view === "week") d.setDate(d.getDate() + 7);
      else d.setDate(d.getDate() + 1);
      return d;
    });
  };

  // Month view
  const renderMonthView = () => {
    const cells = [];

    // Add blank cells so day 1 lands on the correct weekday column
    const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push(<div key={`blank-${i}`} className="calendar-day calendar-day--empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(currentYear, currentMonth, day);
      const key = formatDateKey(dateObj);
      const todaysEvents = eventsByDateKey[key] || [];

      cells.push(
        <div key={day} className="calendar-day">
          <div className="day-number">{day}</div>

          <div>
            {todaysEvents.slice(0, 3).map((event, idx) => (
              <div
                key={idx}
                className="event-dot"
                onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                onMouseLeave={handleEventMouseLeave}
                onClick={() => handleEventClick(event)}
              >
                <span className="dot"></span>
                <span className="event-title">{event.name}</span>
              </div>
            ))}

            {todaysEvents.length > 3 && (
              <div className="more-text">+ {todaysEvents.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }

    return cells;
  };

  // Week view
  const renderWeekView = () => {
    const cells = [];

    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);

      const key = formatDateKey(dayDate);
      const todaysEvents = eventsByDateKey[key] || [];

      cells.push(
        <div key={key} className="calendar-day">
          <div className="day-number">
            {dayDate.getMonth() + 1}/{dayDate.getDate()}
          </div>

          <div>
            {todaysEvents.slice(0, 4).map((event, idx) => (
              <div
                key={idx}
                className="event-dot"
                onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                onMouseLeave={handleEventMouseLeave}
                onClick={() => handleEventClick(event)}
              >
                <span className="dot"></span>
                <span className="event-title">{event.name}</span>
              </div>
            ))}

            {todaysEvents.length > 4 && (
              <div className="more-text">+ {todaysEvents.length - 4} more</div>
            )}
          </div>
        </div>
      );
    }

    return cells;
  };

  // Day view
  const renderDayView = () => {
    const key = formatDateKey(currentDate);
    const todaysEvents = eventsByDateKey[key] || [];

    return (
      <div className="calendar-day">
        <div className="day-number day-view-label">
          {MONTH_NAMES[currentMonth]} {currentDate.getDate()}, {currentYear}
        </div>

        <div className="day-view-events">
          {todaysEvents.length === 0 && (
            <div className="no-events-text">No events for this day.</div>
          )}

          {todaysEvents.map((event) => (
            <div
              key={event.id}
              className="day-view-event-card"
              onClick={() => handleEventClick(event)}
            >
              {/* Event image in day view */}
              {event.image_url ? (
                <img src={event.image_url} alt={event.name} className="day-view-event-image" />
              ) : (
                <div className="day-view-event-image-placeholder" />
              )}

              <div className="day-view-event-title">{event.name}</div>

              {event.address && (
                <div className="day-view-event-line">{event.address}</div>
              )}

              {(event.startTime || event.endTime) && (
                <div className="day-view-event-line">
                  {event.startTime} {event.endTime && `- ${event.endTime}`}
                </div>
              )}

              {event.description && (
                <div className="day-view-event-desc">{event.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  let gridContent;
  if (view === "month") gridContent = renderMonthView();
  else if (view === "week") gridContent = renderWeekView();
  else gridContent = renderDayView();

  const viewLabel = view.charAt(0).toUpperCase() + view.slice(1);
  const isPastEvent = useMemo(() => {
    if (!selectedEvent) return false;

    const now = new Date();
    
    // 1. Parse the Event Date (YYYY-MM-DD)
    const [year, month, day] = selectedEvent.date.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    
    // 2. If the date is strictly before today, it's past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (eventDate < today) return true;

    // 3. If it's strictly after today, it's NOT past
    if (eventDate > today) return false;

    // 4. If it's TODAY, check the end time
    if (selectedEvent.endTime) {
      const [hours, minutes] = selectedEvent.endTime.split(':').map(Number);
      // Create a date object for Today at the Event's End Time
      const eventEndTime = new Date(year, month - 1, day, hours, minutes);
      
      return now > eventEndTime;
    }

    // If it's today but no end time is provided, assume it's available all day
    return false;
  }, [selectedEvent]);

  return (
    <div className="home-container">
      <div className="home-card">
        <div className="calendar-container" ref={containerRef}>
          {/* App title banner */}
          <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
          </div>

          {/* Top nav bar */}
          <div className="calendar-top-nav">
            <div className="nav-left">
              <button className="nav-tab active">Calendar</button>
              <button className="nav-tab" onClick={() => onViewSelect("my-events")}>
                My Events
              </button>
              <button className="nav-tab" onClick={() => onViewSelect("connections")}>
                My Connections
              </button>
            </div>

          <div className="nav-right">
            <button
              type="button"
              className="nav-icon-circle nav-icon-button nav-notification-button"
              aria-label="Notifications"
              onClick={() => onViewSelect("notifications")}
            >
              <svg
                className="nav-bell-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>

              {hasUnreadNotifications && <span className="nav-notification-dot" />}
            </button>

            <div className="nav-avatar-circle">
              <button
                className="nav-tab nav-profile-button"
                onClick={() => onViewSelect("user-profile")}
                aria-label="Profile"
              >
                <svg
                  className="nav-user-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
          </div>

          {needsProfileSetup && showSetupGateModal && (
            <>
              <div className="setup-gate-backdrop" />
              <div className="setup-gate-modal" role="dialog" aria-modal="true">
                <h2 className="setup-gate-title">Set up your profile</h2>
                <p className="setup-gate-subtitle">
                  You need to set up your profile before you can say “Yes” to events.
                </p>

                <div className="setup-gate-actions">
                  <button
                    className="setup-gate-primary"
                    onClick={() => onViewSelect("user-profile")}
                  >
                    Set up profile
                  </button>

                  <button
                    className="setup-gate-secondary"
                    onClick={() => {
                      setShowSetupGateModal(false);
                      setDismissedSetupToast(true);
                    }}
                  >
                    Later
                  </button>
                </div>
              </div>
            </>
          )}

          {showYesBlockedModal && (
            <>
              <div
                className="setup-gate-backdrop"
                onClick={() => setShowYesBlockedModal(false)}
              />
              <div className="setup-gate-modal" role="dialog" aria-modal="true">
                <h2 className="setup-gate-title">Profile setup required</h2>
                <p className="setup-gate-subtitle">
                  Please set up your profile before you can say “Yes” to events.
                </p>

                <div className="setup-gate-actions">
                  <button
                    className="setup-gate-primary"
                    onClick={() => onViewSelect("user-profile")}
                  >
                    Set up profile
                  </button>

                  <button
                    className="setup-gate-secondary"
                    onClick={() => setShowYesBlockedModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ===== Safety Modal ===== */}
          {showSafetyModal && (
            <div className="safety-overlay" role="dialog" aria-modal="true">
              <div className="safety-modal">
                <div className="safety-title">Safety reminder</div>
                <div className="safety-text">
                  Be careful when connecting with people you don’t know. Don’t share personal
                  information like your address, passwords, financial info, or private details.
                  If something feels off, don’t continue.
                </div>

                <div className="safety-actions">
                  <button className="connection-btn secondary" onClick={cancelSafety}>
                    Cancel
                  </button>
                  <button className="connection-btn" onClick={confirmSafetyContinue}>
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Search bar */}
          <div className="calendar-search-row">
            <div className="search-box" ref={searchBoxRef}>
              <Search className="icon-search" />

              <input
                placeholder="Search for Event"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchOpen(true);
                  setHighlightIndex(-1);
                }}
                onFocus={() => {
                  if (searchQuery.trim()) setIsSearchOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (!isSearchOpen) setIsSearchOpen(true);
                    setHighlightIndex((prev) =>
                      Math.min(prev + 1, suggestions.length - 1)
                    );
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightIndex((prev) => Math.max(prev - 1, 0));
                  } else if (e.key === "Escape") {
                    setIsSearchOpen(false);
                    setHighlightIndex(-1);
                  }
                }}
              />

              {isSearchOpen && suggestions.length > 0 && (
                <div className="search-results">
                  {suggestions.map((evt, idx) => (
                    <div
                      key={evt.id ?? `${evt.name}-${evt.date}-${idx}`}
                      className={
                        "search-result-item" +
                        (idx === highlightIndex ? " active" : "")
                      }
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectEventFromSearch(evt);
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{evt.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.75 }}>
                        {formatDisplayDate(evt.date)}
                        {evt.address ? ` • ${evt.address}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Month header */}
          <div className="calendar-month-row">
            <div className="month-nav">
              <button className="chevron-button" onClick={goToPrev}>
                &lt;
              </button>

              <span className="month-label">
                {MONTH_NAMES[currentMonth]} {currentYear}
              </span>

              <button className="chevron-button" onClick={goToNext}>
                &gt;
              </button>
            </div>

            <div className="month-actions">
              <div className="view-dropdown">
                <button
                  className="view-button"
                  onClick={() => setIsViewMenuOpen((prev) => !prev)}
                >
                  {viewLabel}
                  <span className="view-caret">▾</span>
                </button>

                {isViewMenuOpen && (
                  <div className="view-menu">
                    {["month", "week", "day"].map((option) => (
                      <div
                        key={option}
                        className={
                          "view-menu-item" + (view === option ? " active" : "")
                        }
                        onClick={() => {
                          setView(option);
                          setIsViewMenuOpen(false);
                        }}
                      >
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="filter-dropdown" ref={filterRef}>
                <button
                  className="filter-button"
                  onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                  type="button"
                >
                  Filters{" "}
                  {selectedFilters.length > 0 ? `(${selectedFilters.length})` : ""}{" "}
                  <Filter size={16} />
                </button>

                {isFilterMenuOpen && (
                  <div className="filter-menu">
                    {FILTER_OPTIONS.map((opt) => {
                      const checked = selectedFilters.includes(opt.value);
                      return (
                        <label key={opt.value} className="filter-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFilters((prev) => [...prev, opt.value]);
                              } else {
                                setSelectedFilters((prev) =>
                                  prev.filter((v) => v !== opt.value)
                                );
                              }
                            }}
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}

                    <div className="filter-actions">
                      <button
                        type="button"
                        className="filter-clear"
                        onClick={() => setSelectedFilters([])}
                        disabled={selectedFilters.length === 0}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="filter-done"
                        onClick={() => setIsFilterMenuOpen(false)}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* weekday header */}
          {(view === "month" || view === "week") && (
            <div className="calendar-weekdays">
              {WEEKDAY_NAMES.map((day) => (
                <div key={day} className="weekday-cell">
                  {day}
                </div>
              ))}
            </div>
          )}

          {/* Calendar grid */}
          <div className={`calendar-grid ${view}-view`}>{gridContent}</div>

          {/* Hover popup */}
          {hoveredEvent && hoverPosition && !selectedEvent && (
            <div
              className="event-hover-card"
              style={{ top: hoverPosition.top, left: hoverPosition.left }}
            >
              {/* Image in hover card */}
              {hoveredEvent.image_url ? (
                <img src={hoveredEvent.image_url} alt={hoveredEvent.name} className="hover-event-image" />
              ) : (
                <div className="hover-image-placeholder">No Image</div>
              )}
              <div className="hover-event-body">
                <div className="hover-event-title">{hoveredEvent.name}</div>
                <div className="hover-event-line">
                  <span className="hover-label">Date: </span>
                  {formatDisplayDate(hoveredEvent.date)}
                </div>
                {(hoveredEvent.startTime || hoveredEvent.endTime) && (
                  <div className="hover-event-line">
                    <span className="hover-label">Time: </span>
                    {hoveredEvent.startTime}
                    {hoveredEvent.endTime && ` - ${hoveredEvent.endTime}`}
                  </div>
                )}
                {hoveredEvent.address && (
                  <div className="hover-event-line">
                    <span className="hover-label">Location: </span>
                    {hoveredEvent.address}
                  </div>
                )}
                {hoveredEvent.description && (
                  <div className="hover-event-desc">{hoveredEvent.description}</div>
                )}
              </div>
            </div>
          )}

          {/* Side panel */}
          {selectedEvent && (
            <>
            
              <div className="event-detail-backdrop" onClick={closeDetailPanel}></div>

              <div className="event-detail-panel">
                <button className="detail-close-button" onClick={closeDetailPanel}>×</button>

                {selectedEvent.image_url
                  ? <img src={selectedEvent.image_url} alt={selectedEvent.name} className="detail-event-image" />
                  : <div className="detail-image-placeholder">No Image</div>
                }

                <div className="event-detail-content">
                  <h2 className="detail-title">{selectedEvent.name}</h2>

                  <div className="detail-line">
                    <span className="detail-label">Event Owner: </span>
                    <span className="event-owner-link" onClick={() => selectedEvent.event_owner && setViewingBusinessId(selectedEvent.event_owner)}>
                      {selectedEvent.event_owner}
                    </span>
                  </div>

                  <div className="detail-line">
                    <span className="detail-label">Date: </span>
                    {formatDisplayDate(selectedEvent.date)}
                  </div>

                  {(selectedEvent.startTime || selectedEvent.endTime) && (
                    <div className="detail-line">
                      <span className="detail-label">Time: </span>
                      {selectedEvent.startTime}
                      {selectedEvent.endTime && ` to ${selectedEvent.endTime}`}
                    </div>
                  )}

                  {selectedEvent.admission && (
                    <div className="detail-line">
                      <span className="detail-label">Admission: </span>
                      {selectedEvent.admission}
                    </div>
                  )}

                  {selectedEvent.location && (
                    <div className="detail-line">
                      <span className="detail-label">Location: </span>
                      {selectedEvent.location}
                    </div>
                  )}

                  {selectedEvent.description && (
                    <p className="detail-description">{selectedEvent.description}</p>
                  )}

                </div>

                {/* Footer: question + Yes + Save for Later */}
                {!isPastEvent && (
                  <div className="event-detail-footer">
                    <span className="go-question">Do you wanna go?</span>

                    <button
                      className={"yes-button" + (isSelectedYes ? " active" : "")}
                      onClick={() => toggleYes(selectedEvent.id)}
                    >
                      {isSelectedYes ? "Going" : "Yes"}
                    </button>

                    <button
                      className={"bookmark-toggle" + (isSelectedBookmarked ? " active" : "")}
                      onClick={() => toggleBookmark(selectedEvent.id)}
                    >
                      <Heart className="bookmark-icon" />
                      <span>{isSelectedBookmarked ? "Saved" : "Save for Later"}</span>
                    </button>
                  </div>
                )}

                {/* Optional: Show a subtle message if it is a past event */}
                {isPastEvent && (
                  <div className="event-detail-footer past-notice">
                    <span className="go-question" style={{ opacity: 0.6 }}>This event has ended</span>
                  </div>
                )}

                                                {/* People going card */}
                <div className="event-social-row">
                  <div className="event-social-card event-social-card--single">
                    <div className="event-social-title">Who’s going</div>

                    {socialLoading ? (
                      <div className="event-social-muted">Loading…</div>
                    ) : (
                      <>
                        <div className="event-social-subtitle">Your Connections</div>
                        {connectionsGoing.length === 0 ? (
                          <div className="event-social-muted">No connections marked going.</div>
                        ) : (
                          <ul className="event-social-list">
                            {connectionsGoing.map((u) => (
                              <li key={u.user_id || u.id} className="event-social-item">
                                <span className="event-social-name">{userLabel(u)}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="event-social-subtitle">Other people going</div>
                        {suggestedGoing.length === 0 ? (
                          <div className="event-social-muted">No other attendees yet.</div>
                        ) : (
                          <ul className="event-social-list">
                            {suggestedGoing.map((u) => {
                              const uid = String(u.user_id || u.id || "");
                              return (
                                <li
                                  key={uid}
                                  className="event-social-item event-social-item--invite"
                                >
                                  <span className="event-social-name">{userLabel(u)}</span>
                                  {(() => {
                                    const isAdded = addedConnectionIds.includes(uid);
                                    return (
                                      <button
                                        type="button"
                                        className={`event-invite-btn ${isAdded ? "event-invite-btn--added" : ""}`}
                                        onClick={() => {
                                          if (!isAdded) requestAddConnection(uid);
                                        }}
                                        disabled={isAdded}
                                      >
                                        {isAdded ? "Added" : "Add"}
                                      </button>
                                    );
                                  })()}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </div>

                </div>

            </>
          )}
        </div>
      </div>
      {viewingBusinessId && (
        <div className="business-full-popup-overlay">
          <div className="business-full-popup-container">
            <button className="bp-close-btn" onClick={() => setViewingBusinessId(null)}>×</button>
            <BusinessProfile 
              businessId={viewingBusinessId} 
              readOnly={true} 
            />
          </div>
        </div>
      )}
    </div>
  );
}